import { useMemo } from 'react';
import type { Booking, BookingPaymentType, TableEmbed } from '../../lib/types';
import { POLICY } from '../../lib/policy';
import { nowMinutesLocal, minutesToHm, formatClock } from '../../lib/time';
import { PS, T } from '../../lib/theme';
import { fullName } from '../../lib/format';

// Display window for the grid — driven by policy constants.
const OPEN_HOUR = POLICY.OPEN_HOUR;
const CLOSE_HOUR = POLICY.CLOSE_HOUR;
const SLOT_MINUTES = POLICY.SLOT_STEP_MINUTES;
const SLOTS_TOTAL = ((CLOSE_HOUR - OPEN_HOUR) * 60) / SLOT_MINUTES;
// How many slots make up one hour (used for hour-boundary lines).
const SLOTS_PER_HOUR = 60 / SLOT_MINUTES;
// Slots of padding kept around "now" so the live cursor always has context.
const NOW_PAD = (2 * 60) / SLOT_MINUTES; // 2 hours

// Slot row height (px) and header row height (px). Kept small so the grid
// stays dense instead of spanning a 24-hour scroll.
const ROW_PX = 16;
const HEADER_PX = 28;

const PAYMENT_COLORS: Record<BookingPaymentType, { bg: string; border: string; fg: string }> = {
  one_time:     { bg: '#d9eef5', border: PS.teal,    fg: PS.tealDeep },
  subscription: { bg: PS.successSoft, border: PS.success, fg: PS.success },
  credit:       { bg: PS.infoBg,  border: PS.infoBorder,  fg: PS.infoText },
  coach_block:  { bg: PS.pendingBg, border: PS.pendingBorder, fg: PS.pendingText },
  tournament_block: { bg: PS.tournamentBg, border: PS.tournamentBorder, fg: PS.tournamentText },
};

/**
 * Neutral styling for a payment type this build does not know about. The map
 * above is keyed by a hand-maintained union, so an enum value added on the API
 * side type-checks fine here and then reads back `undefined` at runtime. Since
 * the lookup happens inside a `.map`, throwing takes down the entire grid — not
 * just the one block — so an unknown type must degrade, never crash.
 */
const UNKNOWN_PAYMENT_COLOR = { bg: PS.surfaceDisabled, border: PS.border, fg: PS.textSec };

interface Props {
  tables: TableEmbed[];
  bookings: Booking[];
  /** YYYY-MM-DD — used to detect prev-day overnight carry-overs. */
  queryDate: string;
  /** Whether queryDate is today (Asia/Tbilisi) — drives the now-line. */
  isToday: boolean;
  onSelect: (b: Booking) => void;
}

function hmToMinutes(hms: string): number {
  // "HH:MM" or "HH:MM:SS"
  const [h, m] = hms.split(':').map(Number);
  return h * 60 + m;
}

function slotFromMinutes(minutes: number): number {
  return Math.round((minutes - OPEN_HOUR * 60) / SLOT_MINUTES);
}

function formatSlotLabel(slotIndex: number): string {
  return formatClock(minutesToHm(OPEN_HOUR * 60 + slotIndex * SLOT_MINUTES));
}

interface Block {
  b: Booking;
  startSlot: number;
  endSlot: number;
}

export function BookingGrid({ tables, bookings, queryDate, isToday, onSelect }: Props) {
  const sortedTables = useMemo(
    () => [...tables].sort((a, b) => a.display_order - b.display_order),
    [tables],
  );
  const tableIdToColumn = useMemo(() => {
    const m = new Map<number, number>();
    // Column 1 = time labels. Tables start at column 2.
    sortedTables.forEach((t, i) => m.set(t.id, i + 2));
    return m;
  }, [sortedTables]);

  // Confirmed bookings on visible tables, resolved to absolute slot positions.
  const blocks = useMemo<Block[]>(() => {
    const out: Block[] = [];
    for (const b of bookings) {
      if (b.status !== 'confirmed' || !tableIdToColumn.has(b.table_id)) continue;
      const isPrevDay = b.date < queryDate;
      const startMin = isPrevDay ? 0 : hmToMinutes(b.start_time);
      const endMin = hmToMinutes(b.end_time);
      // Handle 24:00 wraparound (end_time can be "24:00:00" or "00:00:00").
      // Prev-day carry-overs have end_time < start_time by definition.
      const endMinAdj = !isPrevDay && endMin <= startMin ? endMin + 24 * 60 : endMin;
      const startSlot = Math.max(0, slotFromMinutes(startMin));
      const endSlot = Math.min(SLOTS_TOTAL, slotFromMinutes(endMinAdj));
      if (endSlot <= startSlot) continue; // entirely outside the day window
      out.push({ b, startSlot, endSlot });
    }
    return out;
  }, [bookings, tableIdToColumn, queryDate]);

  // Current minute-of-day, only meaningful when viewing today.
  const nowMinutes = isToday ? nowMinutesLocal() : null;

  // The contiguous visible slot range = bookings span ∪ now±2h, clamped to
  // the day. Null means: no bookings and not today → empty state.
  const window = useMemo(() => {
    let start = Infinity;
    let end = -Infinity;
    for (const blk of blocks) {
      start = Math.min(start, blk.startSlot);
      end = Math.max(end, blk.endSlot);
    }
    if (nowMinutes != null) {
      const nowSlot = nowMinutes / SLOT_MINUTES;
      start = Math.min(start, Math.floor(nowSlot) - NOW_PAD);
      end = Math.max(end, Math.ceil(nowSlot) + NOW_PAD);
    }
    if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
    return {
      start: Math.max(0, Math.floor(start)),
      end: Math.min(SLOTS_TOTAL, Math.ceil(end)),
    };
  }, [blocks, nowMinutes]);

  if (!window) {
    return (
      <div
        style={{
          background: PS.white,
          border: `1px solid ${PS.borderLight}`,
          borderRadius: 4,
          padding: 32,
          textAlign: 'center',
          ...T.caption,
          color: PS.textSec,
        }}
      >
        No bookings for this date.
      </div>
    );
  }

  const { start: visStart, end: visEnd } = window;
  const visibleCount = visEnd - visStart;
  // Absolute slot indices currently rendered.
  const visibleSlots = Array.from({ length: visibleCount }, (_, i) => visStart + i);

  // gridTemplateColumns: time-label col is fixed; table cols share the rest.
  const cols = `48px repeat(${sortedTables.length}, minmax(84px, 1fr))`;
  const rows = `${HEADER_PX}px repeat(${visibleCount}, ${ROW_PX}px)`;

  // Now-line placement (today only). Sits on the row containing `now`,
  // nudged by the sub-slot fraction so it lands on the exact minute.
  let nowRow: number | null = null;
  let nowOffset = 0;
  if (nowMinutes != null) {
    const nowSlot = nowMinutes / SLOT_MINUTES;
    nowRow = 2 + (Math.floor(nowSlot) - visStart);
    nowOffset = (nowSlot - Math.floor(nowSlot)) * ROW_PX;
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: cols,
        gridTemplateRows: rows,
        background: PS.white,
        border: `1px solid ${PS.borderLight}`,
        borderRadius: 4,
        overflowX: 'auto',
        // overflow-x:auto implicitly makes overflow-y `auto`, which traps
        // vertical page scroll on touch. Pin it to hidden (height fits).
        overflowY: 'hidden',
        minWidth: 320,
      }}
    >
      {/* Header: empty top-left + table names */}
      <div
        style={{
          gridRow: 1,
          gridColumn: 1,
          borderBottom: `1px solid ${PS.borderLight}`,
          borderRight: `1px solid ${PS.borderLight}`,
        }}
      />
      {sortedTables.map((t, i) => (
        <div
          key={t.id}
          style={{
            gridRow: 1,
            gridColumn: i + 2,
            borderBottom: `1px solid ${PS.borderLight}`,
            padding: '6px 8px',
            ...T.label,
            color: PS.nearBlack,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {t.name}
        </div>
      ))}

      {/* Time labels in column 1, one row per visible slot, hour boundaries only */}
      {visibleSlots.map((absSlot, i) => {
        const isHour = absSlot % SLOTS_PER_HOUR === 0;
        return (
          <div
            key={`label-${absSlot}`}
            style={{
              gridRow: i + 2,
              gridColumn: 1,
              borderRight: `1px solid ${PS.borderLight}`,
              borderTop: isHour ? `1px solid ${PS.borderLight}` : '1px dashed transparent',
              ...T.microLabel,
              color: PS.textSec,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              padding: isHour ? '1px 4px 0 0' : 0,
              visibility: isHour ? 'visible' : 'hidden',
            }}
          >
            {isHour ? formatSlotLabel(absSlot) : ''}
          </div>
        );
      })}

      {/* Grid background cells (one per visible slot × table) for the hour lines */}
      {sortedTables.map((t, ci) =>
        visibleSlots.map((absSlot, i) => (
          <div
            key={`cell-${t.id}-${absSlot}`}
            style={{
              gridRow: i + 2,
              gridColumn: ci + 2,
              borderTop: absSlot % SLOTS_PER_HOUR === 0 ? `1px solid ${PS.borderLight}` : 'none',
              borderRight: ci < sortedTables.length - 1 ? `1px solid ${PS.borderLight}` : 'none',
            }}
          />
        )),
      )}

      {/* Booking blocks */}
      {blocks.map(({ b, startSlot, endSlot }) => {
        const rowStart = 2 + Math.max(0, startSlot - visStart);
        const rowEnd = 2 + Math.min(visibleCount, endSlot - visStart);
        if (rowEnd <= rowStart) return null; // outside the visible window
        const col = tableIdToColumn.get(b.table_id)!;
        const c = PAYMENT_COLORS[b.payment_type] ?? UNKNOWN_PAYMENT_COLOR;
        const userName = b.users ? fullName(b.users) : null;
        const slotLabel = b.payment_type === 'tournament_block'
          ? b.tournament_title?.trim() || 'Tournament'
          : userName ?? '—';
        const tooltipLabel = b.payment_type === 'tournament_block'
          ? slotLabel
          : userName ?? '?';
        return (
          <button
            key={b.id}
            onClick={() => onSelect(b)}
            style={{
              gridRow: `${rowStart} / ${rowEnd}`,
              gridColumn: col,
              margin: 1,
              padding: '2px 6px',
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderRadius: 3,
              cursor: 'pointer',
              textAlign: 'left',
              overflow: 'hidden',
              ...T.caption,
              // After the spread so the payment-type color wins over
              // T.caption's default gray.
              color: c.fg,
            }}
            title={`${tooltipLabel} · ${formatClock(b.start_time)}–${formatClock(b.end_time)} · ${b.payment_type}`}
          >
            <div style={{ ...T.bodyBold, fontSize: 12, lineHeight: 1.1, color: c.fg }}>
              {slotLabel}
            </div>
            <div style={{ ...T.microLabel, color: c.fg }}>
              {formatClock(b.start_time)}–{formatClock(b.end_time)}
            </div>
          </button>
        );
      })}

      {/* Now-line — today only, drawn over cells and blocks. */}
      {nowRow != null && (
        <div
          style={{
            gridRow: nowRow,
            gridColumn: '1 / -1',
            alignSelf: 'start',
            transform: `translateY(${nowOffset}px)`,
            height: 0,
            borderTop: `2px solid ${PS.red}`,
            position: 'relative',
            zIndex: 5,
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: -1,
              transform: 'translateY(-50%)',
              background: PS.red,
              ...T.microLabel,
              color: PS.white,
              padding: '0 4px',
              borderRadius: 2,
            }}
          >
            now
          </span>
        </div>
      )}
    </div>
  );
}
